import RecordingStorage from '@/api/RecordingStorage';

// Objetos nativos do navegador (MediaRecorder, streams, AudioContext) ficam
// fora do state do Vuex para não virarem reativos e para sobreviverem à
// destruição do SessionReportForm ao trocar de tela.
let engine = {
  stream: null,
  micStream: null,
  screenStream: null,
  audioContext: null,
  mediaRecorder: null,
  chunks: [],
  recorderBlob: null,
  recordingTimer: null,
  meterContext: null,
  meterAnalyser: null,
  meterData: null,
  meterRaf: null,
  checkpointTimer: null,
  checkpointSeq: 0,
  checkpointedChunkCount: 0,
  recordingStartedAt: null,
};

function meterTick(commit) {
  if (!engine.meterAnalyser) return;
  engine.meterAnalyser.getByteFrequencyData(engine.meterData);
  const avg = engine.meterData.reduce((sum, v) => sum + v, 0) / engine.meterData.length;
  commit('setAudioLevel', avg / 255);
  engine.meterRaf = requestAnimationFrame(() => meterTick(commit));
}

export default {

  namespaced: true,

  state: () => ({
    recorderState: null,        // null | 'mode' | 'online-guide' | 'requesting' | 'recording' | 'stopped' | 'error'
    recordingMode: null,        // null | 'mic' | 'meeting'
    duration: 0,
    audioLevel: 0,             // 0..1, nível de volume captado enquanto grava (para UI reativa ao som)
    isPaused: false,
    recorderErrorMessage: '',
    recordingSession: null,     // { id, customerName } — snapshot da sessão em gravação
    pendingReopenSessionId: null,
    hasRecordedBlob: false,      // espelha (engine.recorderBlob != null) de forma reativa — engine não é reativo de propósito
  }),

  mutations: {
    setRecorderState(state, value) {
      state.recorderState = value;
    },

    setRecordingMode(state, value) {
      state.recordingMode = value;
    },

    setDuration(state, value) {
      state.duration = value;
    },

    incrementDuration(state) {
      state.duration++;
    },

    setRecorderErrorMessage(state, value) {
      state.recorderErrorMessage = value;
    },

    setRecordingSession(state, session) {
      state.recordingSession = session;
    },

    setAudioLevel(state, value) {
      state.audioLevel = value;
    },

    setPaused(state, value) {
      state.isPaused = value;
    },

    setPendingReopenSessionId(state, sessionId) {
      state.pendingReopenSessionId = sessionId;
    },

    setHasRecordedBlob(state, value) {
      state.hasRecordedBlob = value;
    },
  },

  actions: {
    openModeChoice({ commit }) {
      commit('setRecordingMode', null);
      commit('setRecorderState', 'mode');
    },

    chooseMode({ commit, dispatch }, { mode, session }) {
      commit('setRecordingSession', session);
      commit('setRecordingMode', mode);
      if (mode === 'mic') {
        dispatch('openRecorder');
      } else {
        commit('setRecorderState', 'online-guide');
      }
    },

    async openRecorder({ commit, dispatch }) {
      engine.chunks = [];
      engine.recorderBlob = null;
      commit('setHasRecordedBlob', false);
      commit('setDuration', 0);
      commit('setRecorderState', 'requesting');
      await dispatch('requestMic');
    },

    async requestMic({ commit, dispatch }) {
      try {
        engine.stream = await navigator.mediaDevices.getUserMedia({
          audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true },
        });
        dispatch('startRecording');
      } catch {
        commit('setRecorderErrorMessage', 'Não foi possível acessar o microfone. Verifique as permissões do navegador e tente novamente.');
        commit('setRecorderState', 'error');
      }
    },

    startOnlineCapture({ commit, dispatch }) {
      engine.chunks = [];
      engine.recorderBlob = null;
      commit('setHasRecordedBlob', false);
      commit('setDuration', 0);
      commit('setRecorderState', 'requesting');
      dispatch('requestMeetingCapture');
    },

    async requestMeetingCapture({ state, commit, dispatch }) {
      try {
        engine.micStream = await navigator.mediaDevices.getUserMedia({
          audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true },
        });
        if (state.recorderState !== 'requesting') {
          dispatch('releaseStream');
          return;
        }

        engine.screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        if (state.recorderState !== 'requesting') {
          dispatch('releaseStream');
          return;
        }

        if (engine.screenStream.getAudioTracks().length === 0) {
          commit('setRecorderErrorMessage', 'Nenhum áudio foi compartilhado. Ao compartilhar, marque "Compartilhar áudio da guia" (aba do navegador) ou "áudio do sistema" (tela inteira, Windows) e tente novamente.');
          commit('setRecorderState', 'error');
          dispatch('releaseStream');
          return;
        }

        engine.screenStream.getVideoTracks()[0].onended = () => {
          if (state.recorderState === 'recording') dispatch('stopRecording');
        };

        engine.audioContext = new AudioContext();
        await engine.audioContext.resume();
        const destination = engine.audioContext.createMediaStreamDestination();
        engine.audioContext.createMediaStreamSource(engine.micStream).connect(destination);
        engine.audioContext.createMediaStreamSource(engine.screenStream).connect(destination);

        engine.stream = destination.stream;
        dispatch('startRecording');
      } catch {
        commit('setRecorderErrorMessage', 'Não foi possível iniciar a captura. Verifique as permissões de microfone e compartilhamento de tela e tente novamente.');
        commit('setRecorderState', 'error');
        dispatch('releaseStream');
      }
    },

    startRecording({ commit, dispatch, state }) {
      engine.chunks = [];
      engine.checkpointedChunkCount = 0;
      engine.checkpointSeq = 0;
      engine.recordingStartedAt = Date.now();
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
          ? 'audio/ogg;codecs=opus'
          : '';
      engine.mediaRecorder = new MediaRecorder(engine.stream, {
        ...(mimeType ? { mimeType } : {}),
        audioBitsPerSecond: 32000,
      });
      engine.mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) engine.chunks.push(e.data);
      };
      engine.mediaRecorder.onstop = () => {
        dispatch('stopAudioLevelMeter');
        dispatch('releaseStream');
        dispatch('stopCheckpointTimer');
        const blob = new Blob(engine.chunks, { type: engine.mediaRecorder.mimeType });
        if (blob.size === 0) {
          commit('setRecorderErrorMessage', 'A gravação ficou vazia. Verifique se o microfone está capturando áudio e tente novamente.');
          commit('setRecorderState', 'error');
          if (state.recordingSession) dispatch('removePersisted', state.recordingSession.id);
          return;
        }
        engine.recorderBlob = blob;
        commit('setHasRecordedBlob', true);
        dispatch('flushCheckpoint', 'stopped');
        commit('setRecorderState', 'stopped');
        commit('setPaused', false);
      };
      engine.mediaRecorder.start(1000);
      commit('setRecorderState', 'recording');
      commit('setPaused', false);
      dispatch('startRecordingTimer');
      dispatch('startAudioLevelMeter');
      dispatch('startCheckpointTimer');
    },

    startAudioLevelMeter({ commit }) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      engine.meterContext = new AudioCtx();
      const source = engine.meterContext.createMediaStreamSource(engine.stream);
      engine.meterAnalyser = engine.meterContext.createAnalyser();
      engine.meterAnalyser.fftSize = 256;
      engine.meterAnalyser.smoothingTimeConstant = 0.6;
      source.connect(engine.meterAnalyser);
      engine.meterData = new Uint8Array(engine.meterAnalyser.frequencyBinCount);

      meterTick(commit);
    },

    pauseRecording({ commit }) {
      if (engine.mediaRecorder && engine.mediaRecorder.state === 'recording') {
        engine.mediaRecorder.pause();
      }
      clearInterval(engine.recordingTimer);
      engine.recordingTimer = null;
      if (engine.meterRaf) {
        cancelAnimationFrame(engine.meterRaf);
        engine.meterRaf = null;
      }
      commit('setAudioLevel', 0);
      commit('setPaused', true);
    },

    resumeRecording({ commit }) {
      if (engine.mediaRecorder && engine.mediaRecorder.state === 'paused') {
        engine.mediaRecorder.resume();
      }
      engine.recordingTimer = setInterval(() => commit('incrementDuration'), 1000);
      if (engine.meterAnalyser) {
        meterTick(commit);
      }
      commit('setPaused', false);
    },

    stopAudioLevelMeter({ commit }) {
      if (engine.meterRaf) {
        cancelAnimationFrame(engine.meterRaf);
        engine.meterRaf = null;
      }
      engine.meterAnalyser = null;
      engine.meterData = null;
      if (engine.meterContext) {
        engine.meterContext.close();
        engine.meterContext = null;
      }
      commit('setAudioLevel', 0);
    },

    startCheckpointTimer({ dispatch }) {
      engine.checkpointTimer = setInterval(() => {
        dispatch('flushCheckpoint', 'recording');
      }, 20000);
    },

    stopCheckpointTimer() {
      if (engine.checkpointTimer) {
        clearInterval(engine.checkpointTimer);
        engine.checkpointTimer = null;
      }
    },

    async flushCheckpoint({ state }, status) {
      if (!state.recordingSession) return;
      const newChunks = engine.chunks.slice(engine.checkpointedChunkCount);
      if (newChunks.length === 0 && status !== 'stopped') return;

      const sessionId = state.recordingSession.id;
      const mimeType = engine.mediaRecorder ? engine.mediaRecorder.mimeType : '';

      try {
        if (newChunks.length > 0) {
          const batchBlob = new Blob(newChunks, { type: mimeType });
          await RecordingStorage.appendChunk(sessionId, engine.checkpointSeq, batchBlob);
          // Só avança depois de gravar com sucesso: se o append falhar, esses chunks continuam
          // "não persistidos" e entram no próximo checkpoint em vez de serem perdidos silenciosamente.
          engine.checkpointSeq += 1;
          engine.checkpointedChunkCount += newChunks.length;
        }
        await RecordingStorage.saveMeta(sessionId, {
          customerName: state.recordingSession.customerName,
          mode: state.recordingMode,
          startedAt: engine.recordingStartedAt,
          updatedAt: Date.now(),
          status,
          mimeType,
        });
      } catch (err) {
        console.error('Falha ao salvar checkpoint da gravação:', err);
      }
    },

    async removePersisted(_, sessionId) {
      try {
        await RecordingStorage.remove(sessionId);
      } catch (err) {
        console.error('Falha ao remover gravação persistida:', err);
      }
    },

    restoreFromPersisted({ commit }, { sessionId, meta }) {
      return RecordingStorage.reassembleBlob(sessionId, meta.mimeType).then((blob) => {
        engine.recorderBlob = blob;
        commit('setHasRecordedBlob', true);
        engine.checkpointedChunkCount = 0;
        commit('setRecordingMode', meta.mode);
        commit('setRecordingSession', { id: sessionId, customerName: meta.customerName });
        const approxSeconds = meta.startedAt ? Math.round((meta.updatedAt - meta.startedAt) / 1000) : 0;
        commit('setDuration', approxSeconds);
        commit('setRecorderState', 'stopped');
      });
    },

    stopRecording({ dispatch }) {
      dispatch('stopRecordingTimer');
      if (engine.mediaRecorder && engine.mediaRecorder.state !== 'inactive') {
        engine.mediaRecorder.stop();
      }
    },

    startRecordingTimer({ commit }) {
      commit('setDuration', 0);
      engine.recordingTimer = setInterval(() => commit('incrementDuration'), 1000);
    },

    stopRecordingTimer() {
      clearInterval(engine.recordingTimer);
      engine.recordingTimer = null;
    },

    releaseStream() {
      if (engine.stream) {
        engine.stream.getTracks().forEach((t) => t.stop());
        engine.stream = null;
      }
      if (engine.micStream) {
        engine.micStream.getTracks().forEach((t) => t.stop());
        engine.micStream = null;
      }
      if (engine.screenStream) {
        engine.screenStream.getTracks().forEach((t) => t.stop());
        engine.screenStream = null;
      }
      if (engine.audioContext) {
        engine.audioContext.close();
        engine.audioContext = null;
      }
    },

    restartRecording({ dispatch, state }) {
      // "Regravar" volta para a escolha de modo (presencial/online) em vez de reusar o modo anterior.
      if (state.recordingSession) dispatch('removePersisted', state.recordingSession.id);
      dispatch('openModeChoice');
    },

    retryAfterError({ state, dispatch }) {
      // Erro técnico (permissão negada, áudio não compartilhado etc.): tenta de novo no mesmo modo,
      // sem obrigar a pessoa a escolher o modo de novo.
      if (state.recordingMode === 'meeting') {
        dispatch('startOnlineCapture');
      } else {
        dispatch('openRecorder');
      }
    },

    sendRecording() {
      const blob = engine.recorderBlob;
      if (!blob) return null;
      const type = blob.type;
      const ext = type.includes('ogg') ? 'ogg' : type.includes('mp4') ? 'mp4' : 'webm';
      const file = new File([blob], `recording.${ext}`, { type });
      return file;
    },

    getRecorderBlob() {
      return engine.recorderBlob;
    },

    confirmSent({ commit, state, dispatch }) {
      if (state.recordingSession) dispatch('removePersisted', state.recordingSession.id);
      commit('setRecorderState', null);
      commit('setRecordingSession', null);
    },

    cancelRecording({ dispatch, commit }) {
      dispatch('stopRecordingTimer');
      dispatch('stopAudioLevelMeter');
      if (engine.mediaRecorder && engine.mediaRecorder.state !== 'inactive') {
        engine.mediaRecorder.stop();
      }
      dispatch('releaseStream');
      commit('setRecorderState', null);
      commit('setRecordingSession', null);
      commit('setPaused', false);
    },

    newRecording({ commit, dispatch }) {
      commit('setRecordingSession', null);
      dispatch('openModeChoice');
    },

    setPendingReopen({ commit }, sessionId) {
      commit('setPendingReopenSessionId', sessionId);
    },

    clearPendingReopen({ commit }) {
      commit('setPendingReopenSessionId', null);
    },
  },
};
